import { fromEntries } from "../utils/browserCompat";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getPublicProjectConfig } from "../api/projectConfig";
import {
  getStorefrontIdentity,
  storefrontIdentity,
} from "../config/storefrontRuntime";

const ProjectConfigContext = createContext(null);

const OPTIONAL_PROJECT_MODULE_KEYS = Object.freeze([
  "memberships",
  "restaurant_menu",
  "tables",
  "table_reservations",
  "kitchen_orders",
  "recipes",
]);

const PROJECT_CAPABILITY_KEYS = Object.freeze([
  "memberships",
  "gastronomicMenu",
  "tables",
  "tableReservations",
  "kitchenOrders",
  "recipes",
]);

const FALLBACK_CAPABILITIES = Object.freeze(
  fromEntries(PROJECT_CAPABILITY_KEYS.map((key) => [key, false]))
);

function envBoolean(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  return !["0", "false", "off", "no"].includes(
    String(value).trim().toLowerCase()
  );
}

const fallbackEcommerce = envBoolean(
  process.env.REACT_APP_MODULE_ECOMMERCE,
  envBoolean(process.env.REACT_APP_MODULE_PAYMENTS, true)
);
const fallbackSystem = envBoolean(
  process.env.REACT_APP_MODULE_SYSTEM,
  envBoolean(process.env.REACT_APP_MODULE_INVOICING, false)
);

/**
 * Nombres visibles de la switchera:
 * landing   -> Landing
 * products  -> Productos
 * orders    -> Pedidos
 * ecommerce -> E-commerce (alias backend actual: payments)
 * system    -> Sistema (alias backend actual: invoicing)
 * stats     -> Estadísticas
 */
const FALLBACK_MODULES = Object.freeze({
  landing: envBoolean(process.env.REACT_APP_MODULE_LANDING, true),
  products: envBoolean(process.env.REACT_APP_MODULE_PRODUCTS, true),
  orders: envBoolean(process.env.REACT_APP_MODULE_ORDERS, true),
  ecommerce: fallbackEcommerce,
  system: fallbackSystem,
  stats: envBoolean(process.env.REACT_APP_MODULE_STATS, true),
  webpush: envBoolean(process.env.REACT_APP_MODULE_WEBPUSH, false),
  // Compatibilidad con el backend actual.
  payments: fallbackEcommerce,
  invoicing: fallbackSystem,
  ...fromEntries(OPTIONAL_PROJECT_MODULE_KEYS.map((key) => [key, false])),
});

const FALLBACK_CONFIG = Object.freeze({
  project: {
    slug:
      storefrontIdentity.projectKey ||
      storefrontIdentity.storefrontHost ||
      "project",
    name: process.env.REACT_APP_PROJECT_NAME || "Mi negocio",
    plan: "ecommerce",
    status: "active",
  },
  modules: FALLBACK_MODULES,
  capabilities: FALLBACK_CAPABILITIES,
  payments: {
    provider: "mercadopago",
    enabled: fallbackEcommerce,
    connected: Boolean(process.env.REACT_APP_MP_PUBLIC_KEY),
    publicKey: process.env.REACT_APP_MP_PUBLIC_KEY || "",
    locale: process.env.REACT_APP_MP_LOCALE || "es-AR",
    currency: (process.env.REACT_APP_CURRENCY || "ARS").toUpperCase(),
    providers: process.env.REACT_APP_MP_PUBLIC_KEY
      ? [{ provider: "mercadopago", enabled: fallbackEcommerce, connected: true, configured: true, publicKey: process.env.REACT_APP_MP_PUBLIC_KEY || "", locale: process.env.REACT_APP_MP_LOCALE || "es-AR", currency: (process.env.REACT_APP_CURRENCY || "ARS").toUpperCase() }]
      : [],
    availableProviders: process.env.REACT_APP_MP_PUBLIC_KEY ? ["mercadopago"] : [],
  },
  webPush: {
    enabled: false,
    publicKey: "",
  },
  // WP-STANDARD | STABLE
  // El storefront necesita conocer el modo de acceso público para mostrar
  // degradaciones comerciales sin bloquear la landing ni inventar estado local.
  access: {
    mode: "full",
    reason: "fallback",
    billingStatus: "trial",
  },
  theme: {},
});

function pickRoot(payload = {}) {
  return payload?.data?.data || payload?.data || payload || {};
}

function booleanFrom(source, keys, fallback) {
  for (const key of keys) {
    if (typeof source?.[key] === "boolean") return source[key];
  }
  return Boolean(fallback);
}

function normalizeModules(payload = {}, current = FALLBACK_MODULES) {
  const root = pickRoot(payload);
  const project = root?.project || root?.tenant || {};
  const source = root?.modules || project?.modules || {};

  let landing = booleanFrom(source, ["landing"], current?.landing ?? true);
  let products = booleanFrom(source, ["products"], current?.products);
  let orders = booleanFrom(source, ["orders"], current?.orders);
  let ecommerce = booleanFrom(
    source,
    ["ecommerce", "payments"],
    current?.ecommerce ?? current?.payments
  );

  // Landing es el core gratuito. Pedidos todavía compone sobre catálogo,
  // pero Cobros online es transversal y no fuerza Productos/Pedidos.
  landing = true;
  if (orders) products = true;
  const system = booleanFrom(
    source,
    ["system", "invoicing"],
    current?.system ?? current?.invoicing
  );
  const stats = booleanFrom(source, ["stats"], current?.stats);
  const webpush = booleanFrom(source, ["webpush", "webPush"], current?.webpush);

  return {
    landing,
    products,
    orders,
    ecommerce,
    system,
    stats,
    webpush,
    // Alias para rutas/backend existentes.
    payments: ecommerce,
    invoicing: system,
    ...fromEntries(
      OPTIONAL_PROJECT_MODULE_KEYS.map((key) => [
        key,
        booleanFrom(source, [key], current?.[key] ?? false),
      ]),
    ),
  };
}

function normalizeCapabilities(payload = {}, current = FALLBACK_CAPABILITIES) {
  const root = pickRoot(payload);
  const project = root?.project || root?.tenant || {};
  const source = root?.capabilities || project?.capabilities || {};
  return fromEntries(
    PROJECT_CAPABILITY_KEYS.map((key) => [
      key,
      typeof source?.[key] === "boolean" ? source[key] : current?.[key] === true,
    ])
  );
}

function normalizePublicConfig(payload = {}, current = FALLBACK_CONFIG) {
  const root = pickRoot(payload);
  const projectSource = root?.project || root?.tenant || root || {};
  const modules = normalizeModules(root, current?.modules);
  const capabilities = normalizeCapabilities(root, current?.capabilities);
  const paymentSource =
    root?.payments || root?.payment || root?.mercadoPago || root?.mercadopago || {};
  const webPushSource = root?.webPush || root?.webpush || {};

  const currency = String(
    paymentSource?.currency || root?.currency || current?.payments?.currency || "ARS"
  ).toUpperCase();
  const locale =
    paymentSource?.locale ||
    paymentSource?.mpLocale ||
    root?.locale ||
    root?.mpLocale ||
    current?.payments?.locale ||
    (currency === "CLP" ? "es-CL" : "es-AR");
  const hasOwn = (object, key) =>
    Object.prototype.hasOwnProperty.call(object || {}, key);
  const publicKey = hasOwn(paymentSource, "publicKey")
    ? String(paymentSource.publicKey || "")
    : hasOwn(paymentSource, "mpPublicKey")
      ? String(paymentSource.mpPublicKey || "")
      : hasOwn(root, "publicKey")
        ? String(root.publicKey || "")
        : hasOwn(root, "mpPublicKey")
          ? String(root.mpPublicKey || "")
          : current?.payments?.publicKey || "";
  const connected = hasOwn(paymentSource, "connected")
    ? paymentSource.connected === true
    : hasOwn(paymentSource, "configured")
      ? paymentSource.configured === true
      : Boolean(publicKey);
  const sourceProviders = Array.isArray(paymentSource?.providers) ? paymentSource.providers : [];
  const providers = sourceProviders.length
    ? sourceProviders.map((provider) => ({
        ...provider,
        provider: String(provider?.provider || "").toLowerCase(),
        currency: String(provider?.currency || currency).toUpperCase(),
        enabled: modules.ecommerce && provider?.enabled === true,
        connected: provider?.connected === true,
        configured: provider?.configured === true || provider?.connected === true,
        publicKey: String(provider?.publicKey || ""),
        minimumAmount: Number(provider?.minimumAmount || 0),
      }))
    : connected && publicKey
      ? [{ provider: "mercadopago", enabled: modules.ecommerce, connected: true, configured: true, publicKey, locale, currency }]
      : [];
  const availableProviders = providers.filter((provider) => provider.enabled && provider.connected).map((provider) => provider.provider);

  return {
    project: {
      ...current?.project,
      ...projectSource,
      slug:
        projectSource?.slug ||
        root?.projectSlug ||
        current?.project?.slug ||
        FALLBACK_CONFIG.project.slug,
      status: projectSource?.status || current?.project?.status || "active",
    },
    modules,
    capabilities,
    payments: {
      ...current?.payments,
      ...paymentSource,
      provider: paymentSource?.provider || "mercadopago",
      enabled:
        modules.ecommerce &&
        paymentSource?.enabled !== false &&
        paymentSource?.status !== "disabled",
      connected,
      publicKey,
      locale,
      currency,
      providers,
      availableProviders,
      onlineEnabled: availableProviders.length > 0,
    },
    webPush: {
      ...current?.webPush,
      ...webPushSource,
      enabled:
        modules.webpush &&
        webPushSource?.enabled === true &&
        Boolean(webPushSource?.publicKey),
      publicKey: String(webPushSource?.publicKey || ""),
    },
    // WP-STANDARD | TESTED-STATICALLY
    // Conservamos access como dato del backend: es la única fuente de verdad para
    // decidir si corresponde el sello gratuito de WebProfit.
    access: {
      ...current?.access,
      ...(root?.access || {}),
    },
    theme: {
      ...current?.theme,
      ...(projectSource?.theme || root?.theme || {}),
    },
  };
}

function canonicalModuleName(moduleName) {
  const normalized = String(moduleName || "").trim().toLowerCase();
  if (normalized === "payments") return "ecommerce";
  if (normalized === "invoicing") return "system";
  return normalized;
}

function identityKey(identity = {}) {
  return [identity.storefrontHost, identity.projectKey]
    .map((value) => String(value || "").trim().toLowerCase())
    .join("::");
}

function fallbackConfigForIdentity(identity = storefrontIdentity) {
  return {
    ...FALLBACK_CONFIG,
    project: {
      ...FALLBACK_CONFIG.project,
      slug:
        identity.projectKey ||
        identity.storefrontHost ||
        FALLBACK_CONFIG.project.slug,
    },
    modules: { ...FALLBACK_CONFIG.modules },
    capabilities: { ...FALLBACK_CONFIG.capabilities },
    payments: { ...FALLBACK_CONFIG.payments },
    webPush: { ...FALLBACK_CONFIG.webPush },
    theme: { ...FALLBACK_CONFIG.theme },
  };
}

export function ProjectConfigProvider({ children }) {
  const [identity, setIdentity] = useState(() => getStorefrontIdentity());
  const [config, setConfig] = useState(() =>
    fallbackConfigForIdentity(identity)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingFallback, setUsingFallback] = useState(true);
  const [accessVerified, setAccessVerified] = useState(false);
  const [domainBlocked, setDomainBlocked] = useState(false);
  const requestVersionRef = useRef(0);
  const identityProjectKey = identity.projectKey;
  const identityStorefrontHost = identity.storefrontHost;

  const loadConfig = useCallback(async ({ reset = false } = {}) => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    const fallbackConfig = fallbackConfigForIdentity({
      projectKey: identityProjectKey,
      storefrontHost: identityStorefrontHost,
    });

    if (reset) {
      setConfig(fallbackConfig);
      setUsingFallback(true);
      setAccessVerified(false);
    }

    setLoading(true);
    setError("");

    try {
      const payload = await getPublicProjectConfig({
        hostname: identityStorefrontHost,
        slug: identityProjectKey,
      });
      if (requestVersion !== requestVersionRef.current) return null;

      const normalized = normalizePublicConfig(payload, fallbackConfig);
      setConfig(normalized);
      setUsingFallback(false);
      // Nunca interpretar el access de fallback (trial) como un plan real.
      const serverAccess = pickRoot(payload)?.access;
      setAccessVerified(Boolean(
        serverAccess?.mode && serverAccess?.reason && serverAccess?.billingStatus
      ));
      setDomainBlocked(false);
      return normalized;
    } catch (requestError) {
      if (requestVersion !== requestVersionRef.current) return null;

      const responseStatus = Number(requestError?.response?.status || 0);
      const responseCode = String(requestError?.response?.data?.code || "");
      const customDomainBlocked =
        responseStatus === 402 && responseCode === "CUSTOM_DOMAIN_DISABLED";
      // El resolver público continúa respondiendo 404 ante una suspensión:
      // sólo el caso de facturación trae este motivo seguro para la promoción.
      const billingSuspended = responseStatus === 404 &&
        responseCode === "PROJECT_UNAVAILABLE" &&
        requestError?.response?.data?.access?.mode === "suspended" &&
        requestError?.response?.data?.access?.reason === "billing_suspended";
      const message = customDomainBlocked
        ? requestError?.response?.data?.message || "Este dominio personalizado no está activo."
        : responseStatus === 404
          ? "La configuración pública multicliente todavía no está disponible."
          : requestError?.response?.data?.message ||
            requestError?.message ||
            "No se pudo cargar la configuración de la tienda.";

      setConfig((current) => normalizePublicConfig(
        billingSuspended
          ? { access: { mode: "suspended", reason: "billing_suspended" } }
          : {},
        reset ? fallbackConfig : current,
      ));
      setAccessVerified(billingSuspended);
      setUsingFallback(!customDomainBlocked);
      setDomainBlocked(customDomainBlocked);
      setError(message);
      return null;
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [identityProjectKey, identityStorefrontHost]);

  const refreshConfig = useCallback(
    () => loadConfig({ reset: false }),
    [loadConfig]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncIdentity = () => {
      const nextIdentity = getStorefrontIdentity();
      setIdentity((currentIdentity) =>
        identityKey(currentIdentity) === identityKey(nextIdentity)
          ? currentIdentity
          : nextIdentity
      );
    };

    window.addEventListener("hashchange", syncIdentity);
    window.addEventListener("popstate", syncIdentity);
    window.addEventListener("pageshow", syncIdentity);

    return () => {
      window.removeEventListener("hashchange", syncIdentity);
      window.removeEventListener("popstate", syncIdentity);
      window.removeEventListener("pageshow", syncIdentity);
    };
  }, []);

  useEffect(() => {
    loadConfig({ reset: true });

    return () => {
      requestVersionRef.current += 1;
    };
  }, [loadConfig]);

  const isModuleEnabled = useCallback(
    (moduleName) => {
      const canonical = canonicalModuleName(moduleName);
      return config?.modules?.[canonical] === true;
    },
    [config?.modules]
  );

  const storeActive =
    config?.project?.status !== "paused" &&
    config?.project?.status !== "cancelled";
  const isCapabilityEnabled = useCallback(
    (capabilityName) =>
      storeActive && config?.capabilities?.[String(capabilityName || "")] === true,
    [config?.capabilities, storeActive]
  );
  const landingEnabled = storeActive && isModuleEnabled("landing");
  const productsEnabled = landingEnabled && isModuleEnabled("products");
  const ordersEnabled = productsEnabled && isModuleEnabled("orders");
  const ecommerceEnabled = storeActive && isModuleEnabled("ecommerce");
  const systemEnabled = storeActive && isModuleEnabled("system");
  const gastronomicMenuEnabled =
    productsEnabled && isCapabilityEnabled("gastronomicMenu");
  const statsEnabled = storeActive && isModuleEnabled("stats");
  const webPushEnabled =
    storeActive &&
    isModuleEnabled("webpush") &&
    config?.webPush?.enabled === true &&
    Boolean(config?.webPush?.publicKey);
  const onlinePaymentEnabled =
    ecommerceEnabled &&
    Array.isArray(config?.payments?.providers) &&
    config.payments.providers.some(
      (provider) => provider?.enabled === true && provider?.connected === true
    );

  const value = useMemo(
    () => ({
      ...config,
      config,
      loading,
      error,
      usingFallback,
      accessVerified,
      domainBlocked,
      storeActive,
      landingEnabled,
      productsEnabled,
      ordersEnabled,
      ecommerceEnabled,
      systemEnabled,
      gastronomicMenuEnabled,
      statsEnabled,
      webPushEnabled,
      onlinePaymentEnabled,
      paymentReady: onlinePaymentEnabled,
      // Compatibilidad: commerceEnabled continúa representando pedidos.
      // Los cobros online se consultan con onlinePaymentEnabled/paymentReady.
      commerceEnabled: ordersEnabled,
      isModuleEnabled,
      isCapabilityEnabled,
      refreshConfig,
    }),
    [
      config,
      loading,
      error,
      usingFallback,
      accessVerified,
      domainBlocked,
      storeActive,
      landingEnabled,
      productsEnabled,
      ordersEnabled,
      ecommerceEnabled,
      systemEnabled,
      gastronomicMenuEnabled,
      statsEnabled,
      webPushEnabled,
      onlinePaymentEnabled,
      isModuleEnabled,
      isCapabilityEnabled,
      refreshConfig,
    ]
  );

  return (
    <ProjectConfigContext.Provider value={value}>
      {children}
    </ProjectConfigContext.Provider>
  );
}

export function useProjectConfig() {
  const context = useContext(ProjectConfigContext);
  if (!context) {
    throw new Error(
      "useProjectConfig debe usarse dentro de <ProjectConfigProvider />"
    );
  }
  return context;
}

export { FALLBACK_MODULES, normalizePublicConfig };
